/* ── BRIEF 129 §1 — THE LAYER LEARNS WHAT IT IS HOLDING ─────────────────────
 *
 * 127 §4 claimed the drop layer "already sees files and declines them". It does
 * not, and the verdict for that brief measured why: the line it quoted is
 * inside `isOwnRowDrag`, where `return false` means "this is NOT one of our own
 * task rows" — it LETS FILES THROUGH. `looksLikeEmail` accepts
 * `types.includes("Files")` explicitly, so **a Word document dropped on the app
 * today is POSTed to `/resolve-drop`, parsed as an `.eml`, and fails.**
 *
 * This module is what stands between the two. It answers ONE question, from the
 * file's own bytes, before the email resolver is asked anything:
 *
 *     is this an EMAIL, or is it a DOCUMENT?
 *
 * ── THE DISCRIMINATOR, AND WHY IT IS THE BYTES ─────────────────────────────
 *
 * The brief enumerates the candidates and forbids the easy one. Taking them in
 * order, against its own test — "a `.eml` renamed is still an email and a
 * `.docx` is never one":
 *
 *   EXTENSION           refused by the brief, and rightly: it is a label a user
 *                       can change, and Safari's own "download the drag" path
 *                       renames freely.
 *   MIME from the drag  `File.type` is derived BY THE OS FROM THE EXTENSION on
 *                       every platform this app runs on, so it is the same
 *                       evidence wearing a second hat — and it is routinely
 *                       empty (`""`) or `application/octet-stream` for `.msg`.
 *                       Corroboration, never the discriminator.
 *   ASK THE RESOLVER    forbidden by this brief's own fence: "the email
 *                       resolver must not be asked about a Word document".
 *                       A fallback design would also cost a 25 MB round trip
 *                       to learn what four bytes already say.
 *   MAGIC BYTES         what is left, and what is actually true of the file. It
 *                       survives a rename in BOTH directions, which is exactly
 *                       the brief's test, and it costs two `Blob.slice()` reads
 *                       with no network and no dependency.
 *
 * So: the bytes, with the name and the MIME admitted as a LAST-RESORT tiebreak
 * in the one place the bytes are genuinely ambiguous (see OLE2 below).
 *
 * ── AND THE QUESTION IS NOT SYMMETRIC, WHICH IS THE WHOLE DESIGN ───────────
 *
 * "Is this an email?" has a positive test. "Is this a document?" does not, and
 * cannot: a document is any bytes at all. The two errors are not symmetric
 * either —
 *
 *   a document attempted AS AN EMAIL   is destroyed as an intention: the parse
 *                                      fails and the user meets `_dropFailed`.
 *                                      This is today's behaviour and it is the
 *                                      defect.
 *   an email filed AS A DOCUMENT       is a `.eml` sitting in the matter's
 *                                      folder, byte-for-byte, openable in
 *                                      Outlook. Not what was wanted, but
 *                                      nothing is lost and nothing is claimed.
 *
 * Therefore EMAIL requires a positive test and DOCUMENT is the residue. That is
 * not a guess dressed as an answer: it is the direction in which being wrong is
 * recoverable, and the chooser NAMES the file it is about to file, so the
 * residue is never silent (the brief's rule).
 *
 * ── WHAT `unknown` IS, AND WHY IT IS EXACTLY TWO THINGS ────────────────────
 *
 * "A file that cannot be classified must not silently become one or the other."
 * So `unknown` exists — and it is deliberately NARROW, because a wide unknown
 * would refuse ordinary documents (a `.txt` note has no magic number at all and
 * is obviously a document).
 *
 * It has two members, and both are refusals with their own sentence:
 *
 *   "empty"           zero bytes. Neither an email nor a document — the upload
 *                     route refuses it (`generic.cannot_be_empty`) and the
 *                     email resolver refuses it too. Caught here so the user
 *                     gets a sentence instead of a 400.
 *   "ole2-unreadable" a Compound File Binary container whose own directory
 *                     could not be read. THE ONE genuinely ambiguous container:
 *                     `.msg`, `.doc`, `.xls` and `.ppt` share it, so a CFB file
 *                     that will not say which it is could be either, and the
 *                     name says nothing. This is the only case where guessing
 *                     would be a coin toss rather than a safe residue.
 *
 * Everything else resolves.
 */

/** What the bytes said. `unknown` is a refusal, never a default — see above. */
export type DropFileKind = "email" | "document" | "unknown";

export type DropFileVerdict = {
  kind: DropFileKind;
  /** The evidence, as a stable token. Shown to nobody; carried into the
   *  refusal sentence's key and into the PR's own reasoning. */
  why: string;
};

/* How much of the head is read for the format sniff. 512 covers every magic
   number below AND the whole CFB header; 8 KiB is what the RFC-5322 test needs,
   because a real message's header block routinely runs past 4 KiB once
   `Received:` chains and DKIM signatures are in it. One slice, not two. */
const HEAD_BYTES = 8 * 1024;

/* The CFB directory window. 16 KiB from the first directory sector is enough
   for the root entry (the first 128 bytes of it) and for the `__substg1.0_`
   marker to appear if this is a message; bounded so a 25 MB file cannot make
   this read the whole thing. */
const DIR_WINDOW_BYTES = 16 * 1024;

/* ── magic numbers ─────────────────────────────────────────────────────────
   Written as byte arrays rather than as strings so a reader can check them
   against a hex dump without decoding anything. */
const MAGIC_CFB = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]; // OLE2
const MAGIC_PDF = [0x25, 0x50, 0x44, 0x46, 0x2d]; // "%PDF-"
const MAGIC_ZIP = [0x50, 0x4b]; // "PK" — docx/xlsx/pptx/odt, and plain zips
const MAGIC_RTF = [0x7b, 0x5c, 0x72, 0x74, 0x66]; // "{\rtf"

/* MS-OXMSG §2.1.1 — the root storage CLSID of an Outlook message,
   {00020D0B-0000-0000-C000-000000000046}, in the on-disk byte order (Data1/2/3
   little-endian, Data4 as written). A `.doc` carries {00020906-…}, an `.xls`
   {00020820-…}: same container, different declaration. */
const CLSID_MSG = [
  0x0b, 0x0d, 0x02, 0x00, 0x00, 0x00, 0x00, 0x00,
  0xc0, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x46,
];

/* `__substg1.0_` in UTF-16LE — the prefix every MSG property stream carries
   (MS-OXMSG §2.1.3). Nothing else that lives in a CFB container uses it, so
   finding it in the directory region is a positive identification even when the
   root CLSID is one of the rarer message class ids. */
const MSG_STREAM_MARKER = utf16le("__substg1.0_");

function utf16le(s: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    out.push(c & 0xff, (c >> 8) & 0xff);
  }
  return out;
}

function startsWith(buf: Uint8Array, magic: number[]): boolean {
  if (buf.length < magic.length) return false;
  for (let i = 0; i < magic.length; i++) if (buf[i] !== magic[i]) return false;
  return true;
}

function matchesAt(buf: Uint8Array, at: number, magic: number[]): boolean {
  if (at < 0 || at + magic.length > buf.length) return false;
  for (let i = 0; i < magic.length; i++) if (buf[at + i] !== magic[i]) return false;
  return true;
}

function indexOfBytes(buf: Uint8Array, needle: number[]): number {
  const last = buf.length - needle.length;
  for (let i = 0; i <= last; i++) if (matchesAt(buf, i, needle)) return i;
  return -1;
}

/* ── (1) THE RFC-5322 TEST ─────────────────────────────────────────────────
 *
 * A saved message is a header block, then a blank line, then a body. The block
 * is `field-name ":" value`, where a field name is printable US-ASCII except
 * the colon (RFC 5322 §3.6.8 `ftext`), and a value may FOLD onto lines starting
 * with a space or a tab.
 *
 * The test is deliberately stricter than "it has a colon on line one", because
 * a prose file can begin `Subject: notes`. It requires:
 *
 *   · no NUL in the head           — a NUL is a binary file, never a message;
 *   · a well-formed header block   — every line up to the first blank one is a
 *                                    field or a folded continuation;
 *   · `From:`                      — one of the two fields RFC 5322 §3.6 makes
 *                                    MANDATORY, and the anchor of the test;
 *   · THREE distinct fields total  — a real message has ten to forty. A text
 *                                    file that opens with three field-shaped
 *                                    lines, no blank line between them, one of
 *                                    them `From:`, is not a thing that occurs.
 */
function looksLikeRfc5322(head: Uint8Array): boolean {
  if (head.length === 0) return false;
  /* Latin-1 rather than UTF-8: header bytes are US-ASCII by construction, and a
     lone high byte in a non-conforming header must not throw the decode away. */
  let text = "";
  for (let i = 0; i < head.length; i++) {
    const b = head[i];
    if (b === 0) return false; // a NUL byte — binary, and not a message
    text += String.fromCharCode(b);
  }
  const lines = text.split(/\r\n|\n|\r/);
  const names = new Set<string>();
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line === "") break; // end of the header block
    if (/^[ \t]/.test(line)) {
      // A folded continuation is only legal AFTER a field has opened.
      if (names.size === 0) return false;
      continue;
    }
    const m = /^([\x21-\x39\x3b-\x7e]+):/.exec(line);
    if (!m) {
      /* The last line of the window is almost certainly cut in half by
         HEAD_BYTES, so it is not evidence either way. */
      if (i === lines.length - 1) break;
      return false;
    }
    names.add(m[1].toLowerCase());
  }
  return names.has("from") && names.size >= 3;
}

/* ── (2) THE CFB TEST ──────────────────────────────────────────────────────
 *
 * `.msg`, `.doc`, `.xls` and `.ppt` are all Compound File Binary containers, so
 * the container magic answers nothing on its own. The container's own DIRECTORY
 * does: entry 0 is the root storage and carries a CLSID saying what the file
 * is.
 *
 * The header (MS-CFB §2.2) gives both numbers needed to find it:
 *   offset 0x1E  u16  sector shift   → sector size = 1 << shift (512 or 4096)
 *   offset 0x30  u32  first directory sector
 * and the directory begins at `(sector + 1) * sectorSize`, because sector
 * numbering starts AFTER the 512-byte header.
 *
 * Returns the evidence, or null when the directory could not be read at all —
 * and null is what sends the caller to the name as a last resort.
 */
type CfbEvidence = "email" | "email-streams" | "document";

async function classifyCfb(file: File, head: Uint8Array): Promise<CfbEvidence | null> {
  if (head.length < 0x34) return null;
  const view = new DataView(head.buffer, head.byteOffset, head.byteLength);
  const shift = view.getUint16(0x1e, true);
  if (shift < 7 || shift > 20) return null; // not a sector size this format uses
  const sectorSize = 1 << shift;
  const dirSector = view.getUint32(0x30, true);
  /* 0xFFFFFFFE (ENDOFCHAIN) / 0xFFFFFFFF (FREESECT) and anything past the file
     are not a directory we can reach. */
  if (dirSector >= 0xfffffffa) return null;
  const dirStart = (dirSector + 1) * sectorSize;
  if (dirStart >= file.size) return null;

  let dir: Uint8Array;
  try {
    dir = new Uint8Array(
      await file.slice(dirStart, Math.min(file.size, dirStart + DIR_WINDOW_BYTES)).arrayBuffer(),
    );
  } catch (e) {
    /* Not swallowed: a slice that will not read is exactly the "unreadable
       directory" the caller must be told about, and it says so by returning
       null rather than by pretending the file is a document. */
    console.warn("[dropFileKind] CFB directory unreadable:", (e as Error)?.message || e);
    return null;
  }
  if (dir.length < 128) return null;

  /* The root entry's CLSID lives at offset 0x50 of the 128-byte entry. */
  if (matchesAt(dir, 0x50, CLSID_MSG)) return "email";
  /* A message whose root class id is one of the rarer ones still carries the
     property streams, and their names are in this same directory. */
  if (indexOfBytes(dir, MSG_STREAM_MARKER) >= 0) return "email-streams";
  /* A CFB container that read fine and is NOT a message: legacy Office. */
  return "document";
}

/* The last-resort tiebreak, and the ONLY place a name or a MIME type is
   consulted. It runs when a CFB container would not name itself — never before
   the bytes have been asked. */
function nameSaysEmail(file: File): boolean {
  const n = (file.name || "").toLowerCase();
  const t = (file.type || "").toLowerCase();
  return /\.(msg|eml)$/.test(n) || t === "application/vnd.ms-outlook" || t === "message/rfc822";
}
function nameSaysDocument(file: File): boolean {
  const n = (file.name || "").toLowerCase();
  return /\.(doc|dot|xls|xlt|ppt|pot|pps)$/.test(n);
}

/**
 * Classify a dropped file from its own bytes.
 *
 * Never throws: an unreadable file is `unknown`, which the caller refuses with
 * a sentence. That is deliberate — a classifier that throws inside a `drop`
 * handler is the swallowed-throw shape `no-silent-catch` exists for.
 */
export async function classifyDroppedFile(file: File): Promise<DropFileVerdict> {
  if (!file || file.size === 0) return { kind: "unknown", why: "empty" };

  let head: Uint8Array;
  try {
    head = new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer());
  } catch (e) {
    console.warn("[dropFileKind] head unreadable:", (e as Error)?.message || e);
    return { kind: "unknown", why: "unreadable" };
  }
  if (head.length === 0) return { kind: "unknown", why: "empty" };

  // ── the one genuinely ambiguous container, asked first because it is the
  //    only branch that can end in `unknown` for a non-empty file.
  if (startsWith(head, MAGIC_CFB)) {
    const cfb = await classifyCfb(file, head);
    if (cfb === "email") return { kind: "email", why: "ole2-msg-clsid" };
    if (cfb === "email-streams") return { kind: "email", why: "ole2-msg-streams" };
    if (cfb === "document") return { kind: "document", why: "ole2-not-msg" };
    if (nameSaysEmail(file)) return { kind: "email", why: "ole2-name-msg" };
    if (nameSaysDocument(file)) return { kind: "document", why: "ole2-name-office" };
    return { kind: "unknown", why: "ole2-unreadable" };
  }

  // ── a message saved to disk.
  if (looksLikeRfc5322(head)) return { kind: "email", why: "rfc5322" };

  // ── the document magics, named so the PR can say what was recognised rather
  //    than only what was left over.
  if (startsWith(head, MAGIC_PDF)) return { kind: "document", why: "pdf" };
  if (startsWith(head, MAGIC_ZIP)) return { kind: "document", why: "zip" };
  if (startsWith(head, MAGIC_RTF)) return { kind: "document", why: "rtf" };

  // ── the residue, and it is DOCUMENT by the asymmetry argued in the header:
  //    a `.txt`, a `.csv`, a photograph of a signed page. None of them carry a
  //    magic number, all of them are documents, and none of them is an email —
  //    an email that reached this line would have failed the RFC-5322 test,
  //    which is the only way to BE one.
  return { kind: "document", why: "no-email-signature" };
}
